import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { Form, Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from 'ncoded-component-library';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import InputField from 'components/InputField';
import useCallbackRef from 'hooks/useCallbackRef';
import validate from 'validations';
import { DataRoomRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { getArrayLength, getHasElementsInArray } from 'utils';
import useCallbackAsync from 'hooks/useCallbackAsync';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import useRfiCategory from 'hooks/useRfiCategory';

import './SaveAsTemplateModal.styles.scss';

type SaveAsTemplateModalProps = {
  className?: string;
  rfi: DataRoomRFI;
};

const SaveAsTemplateModal: React.ForwardRefRenderFunction<
  ModalRef,
  SaveAsTemplateModalProps
> = ({ className, rfi }, ref) => {
  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();
  const { getCategoryName } = useRfiCategory();

  const classes = classNames('save-as-template-modal', className);

  const { dataRoomRFICategories } = rfi || {};

  const {
    dataRoom: { _id },
  } = useContext(DataRoomContext);

  const { refetchTemplates, showActionUndo } = useContext(RfiContext);

  const onUndoSaveAsTemplate = useCallback(
    async (templateId: string) => {
      await dataRoomApi.rfiTemplates.remove(_id, templateId);
      refetchTemplates();
    },
    [_id, refetchTemplates],
  );

  const [onSaveAsTemplate] = useCallbackAsync(async ({ name }) => {
    const {
      dataRoomRFICategories,
      notifyOnComplete,
      notificationMedium,
      customNote,
    } = rfi;

    const { data: template } = await dataRoomApi.rfiTemplates.create(_id, {
      name,
      documents: dataRoomRFICategories.map(({ name, period }) => ({
        name,
        period,
      })),
      notifyOnComplete,
      notificationMedium,
      customNote,
    });

    modal?.close();

    showActionUndo(
      t('templateCreated'),
      () => onUndoSaveAsTemplate(template._id),
      refetchTemplates,
    );
  });

  return (
    <Modal
      className={classes}
      ref={modalRef}
      title={t('saveAsTemplateModalTitle')}
    >
      <p className="save-as-template-modal__description">
        {t('saveAsTemplateModalDescription')}
      </p>

      <Form
        onSubmit={onSaveAsTemplate}
        render={({ submitting, invalid, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Field
              label={t('templateName')}
              placeholder={t('nameThisTemplate')}
              name="name"
              component={InputField}
              validate={validate.required()}
              required
            />

            {getHasElementsInArray(dataRoomRFICategories) && (
              <div className="save-as-template-modal__documents">
                <p className="save-as-template-modal__documents__count">
                  {t('templateDocumentsCount', {
                    count: getArrayLength(dataRoomRFICategories),
                  })}
                </p>

                <ul>
                  {dataRoomRFICategories.map(({ _id, name }) => (
                    <li key={_id}>{getCategoryName(name)}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={submitting || invalid}>
                {t('saveTemplate')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(SaveAsTemplateModal);
